/**
 * Telas secundárias do app (acessadas pelo hub "Mais"). São, em sua maioria,
 * leituras do catálogo estático (@caint/data) e dos coeficientes do motor —
 * mantêm o app completo e navegável sem mutações de servidor.
 */
import {
  CATALOGO,
  DIMENSOES_IMS,
  ITENS_PERCEPCAO,
  MACROS,
  RISCOS_REFERENCIAIS,
  SETORES,
  SUP_CAMPO,
  SUP_PRE,
  VERSOES,
} from "@caint/data";
import { EixoChip, Header, PesoChip } from "@caint/design-system";
import { Fragment, type ReactNode, useMemo, useState } from "react";
import { engine } from "../lib/engine.ts";

interface TelaProps {
  onBack: () => void;
}

function Tela({
  title,
  sub,
  onBack,
  children,
}: {
  title: string;
  sub?: string;
  onBack: () => void;
  children: ReactNode;
}) {
  return (
    <Fragment>
      <Header title={title} sub={sub} onBack={onBack} />
      <div className="m-body">{children}</div>
    </Fragment>
  );
}

/* ---------------- Monitoramento (QACI & IMS) — referência honesta ---------------- */
export function Monitoramento({ onBack }: TelaProps) {
  return (
    <Tela title="QACI & IMS" sub="Monitoramento contínuo (por ciclo)" onBack={onBack}>
      <div
        className="m-card pad"
        style={{
          background: "color-mix(in srgb, var(--primary) 6%, white)",
          border: "1px solid color-mix(in srgb, var(--primary) 18%, white)",
          marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          O <b>QACI</b> e o <b>IMS</b> do ciclo ativo aparecem no <b>Início</b> e no <b>Painel da Unidade</b>{" "}
          (valores reais, revalidados por ciclo). Os resultados do formulário público (IMGR) ficam em{" "}
          <b>Ciclos → Formulário &amp; IMGR</b>.
        </div>
      </div>
      <h3 style={{ fontSize: 13, margin: "8px 0 8px", color: "var(--text-muted)" }}>
        IMS — dimensões avaliadas
      </h3>
      <div className="m-card pad">
        {DIMENSOES_IMS.map((d) => (
          <div
            key={d.code}
            style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "0.5px solid var(--border)" }}
          >
            <b style={{ fontFamily: "var(--font-mono)", color: "var(--primary)" }}>{d.code}</b>
            <span style={{ flex: 1, fontSize: 13 }}>{d.nome}</span>
          </div>
        ))}
      </div>
      <p className="spec" style={{ marginTop: 14 }}>
        O detalhamento mensal do QACI (C1/C2/C3 por mês) por ciclo ainda não é coletado — recurso em
        desenvolvimento.
      </p>
    </Tela>
  );
}

/* ---------------- Escalas & Selos (referência do motor) ---------------- */
export function Escalas({ onBack }: TelaProps) {
  const c = engine.config;
  const curva: [string, number][] = Object.entries(c.curvaScore);
  const selos: [string, number][] = [
    ["🥇 Ouro", c.seloOuro],
    ["🥈 Prata", c.seloPrata],
    ["🥉 Bronze", c.seloBronze],
  ];
  const riscos: [string, string][] = [
    ["RP · Pequeno", `≤ ${c.riskRP}`],
    ["RM · Médio", `≤ ${c.riskRM}`],
    ["RA · Alto", `≤ ${c.riskRA}`],
    ["RC · Crítico", `> ${c.riskRA}`],
  ];
  return (
    <Tela title="Escalas & Selos" sub="Coeficientes vigentes (v4.0)" onBack={onBack}>
      <h3 style={{ fontSize: 13, margin: "2px 0 8px", color: "var(--text-muted)" }}>Curva ONA (score)</h3>
      <div className="m-card pad">
        {curva.map(([nivel, score]) => (
          <div key={nivel} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
            <span>
              Nível {nivel} — {engine.situacao(nivel)}
            </span>
            <b style={{ fontFamily: "var(--font-mono)" }}>{engine.num2(score)}</b>
          </div>
        ))}
      </div>
      <h3 style={{ fontSize: 13, margin: "18px 0 8px", color: "var(--text-muted)" }}>Selos QualiEMSERH</h3>
      <div className="m-card pad">
        {selos.map(([nome, lim]) => (
          <div key={nome} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
            <span>{nome}</span>
            <b style={{ fontFamily: "var(--font-mono)" }}>IMGQ ≥ {engine.pct(lim)}</b>
          </div>
        ))}
      </div>
      <h3 style={{ fontSize: 13, margin: "18px 0 8px", color: "var(--text-muted)" }}>
        Níveis de risco (P×I)
      </h3>
      <div className="m-card pad">
        {riscos.map(([nome, lim]) => (
          <div key={nome} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
            <span>{nome}</span>
            <b style={{ fontFamily: "var(--font-mono)" }}>{lim}</b>
          </div>
        ))}
      </div>
    </Tela>
  );
}

/* ---------------- Versionamento ---------------- */
export function Versoes({ onBack }: TelaProps) {
  return (
    <Tela title="Versionamento" sub="Evolução metodológica do instrumento" onBack={onBack}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {VERSOES.map((v) => (
          <div key={v.versao} className="m-card pad">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <b>{v.versao}</b>
              <span className="spec">{v.data}</span>
              <span
                className="m-pill"
                style={{
                  marginLeft: "auto",
                  background: v.status === "vigente" ? "var(--mat-3-soft)" : "var(--surface-2)",
                  color: v.status === "vigente" ? "var(--mat-4)" : "var(--text-muted)",
                  fontSize: 11,
                }}
              >
                {v.status}
              </span>
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 6 }}>{v.resumo}</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "var(--text-muted)" }}>
              {v.mudancas.map((mu) => (
                <li key={mu}>{mu}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Tela>
  );
}

/* ---------------- Matriz de Riscos (referencial COSO) ---------------- */
export function Matriz({ onBack }: TelaProps) {
  return (
    <Tela title="Matriz de Riscos" sub={`${RISCOS_REFERENCIAIS.length} riscos referenciais`} onBack={onBack}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {RISCOS_REFERENCIAIS.map((r) => (
          <div key={r.id} className="m-card pad">
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <span className="chip chip-code" style={{ fontSize: 11 }}>
                {r.id}
              </span>
              <span className="spec">{r.categoria}</span>
              <b style={{ marginLeft: "auto", fontFamily: "var(--font-mono)" }}>P×I {r.p * r.i}</b>
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{r.evento}</div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>
              <b>{r.processo}</b> · {r.coso}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>🛡️ {r.controle}</div>
          </div>
        ))}
      </div>
    </Tela>
  );
}

/* ---------------- Catálogo (251 itens, com busca/filtro) ---------------- */
export function Catalogo({ onBack }: TelaProps) {
  const [busca, setBusca] = useState("");
  const [eixo, setEixo] = useState<"todos" | "IMGO" | "IMQA" | "IMGR">("todos");
  const filtrado = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return CATALOGO.filter(
      (it) =>
        (eixo === "todos" || it.eixo === eixo) &&
        (q === "" || it.pergunta.toLowerCase().includes(q) || it.code.toLowerCase().includes(q)),
    );
  }, [busca, eixo]);
  return (
    <Tela title="Catálogo" sub={`${filtrado.length}/${CATALOGO.length} itens`} onBack={onBack}>
      <input
        className="m-input"
        placeholder="Buscar por código ou pergunta…"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        style={{ marginBottom: 10 }}
      />
      <div className="m-seg" style={{ marginBottom: 14 }}>
        {(["todos", "IMGO", "IMQA", "IMGR"] as const).map((e) => (
          <button key={e} type="button" className={eixo === e ? "on" : ""} onClick={() => setEixo(e)}>
            {e === "todos" ? "Todos" : e}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtrado.slice(0, 120).map((it) => (
          <div key={it.id} className="m-card pad">
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <span className="chip chip-code" style={{ fontSize: 11 }}>
                {it.code}
              </span>
              <PesoChip peso={it.peso} />
              <EixoChip eixo={it.eixo} />
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.35 }}>{it.pergunta}</div>
            <div className="spec" style={{ marginTop: 4 }}>
              {it.norma} · evidência: {it.evidencia}
            </div>
          </div>
        ))}
        {filtrado.length > 120 && (
          <p className="spec" style={{ textAlign: "center" }}>
            Mostrando 120 de {filtrado.length}. Refine a busca.
          </p>
        )}
      </div>
    </Tela>
  );
}

/* ---------------- Percepção de Risco (Aba 16) ---------------- */
export function Percepcao({ onBack }: TelaProps) {
  return (
    <Tela title="Percepção de Risco" sub="Aba 16 — leitura multivocal" onBack={onBack}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {ITENS_PERCEPCAO.map((p) => (
          <div key={p.ordem} className="m-card pad">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <b style={{ fontFamily: "var(--font-mono)", color: "var(--primary)" }}>#{p.ordem}</b>
              <span style={{ flex: 1, fontSize: 13.5 }}>{p.desc}</span>
              <b style={{ fontFamily: "var(--font-mono)" }}>
                P{p.p}·I{p.i}
              </b>
            </div>
          </div>
        ))}
      </div>
    </Tela>
  );
}

/* ---------------- Supervisão (AUDIN) — visão leitura para não-supervisores ---------------- */
export function Supervisao({ onBack }: TelaProps) {
  const FASES = [
    {
      num: "①",
      titulo: "Pré-visita",
      desc: "Verificação de documentos, planejamento e logística antes da chegada à unidade.",
      n: SUP_PRE.length,
    },
    {
      num: "②",
      titulo: "Supervisão em campo",
      desc: "Conduta dos avaliadores durante a visita: método, imparcialidade e evidências.",
      n: SUP_CAMPO.length,
    },
    {
      num: "③",
      titulo: "Re-performance cega",
      desc: "O supervisor re-avalia uma amostra de itens de forma independente, sem ver a nota do avaliador, e compara depois.",
      n: null,
    },
    {
      num: "④",
      titulo: "Validação técnica",
      desc: "Validação documental, in loco e por critério. Itens de alto risco Não Conformes pesam no parecer.",
      n: null,
    },
    {
      num: "⑤",
      titulo: "Contestações",
      desc: "Divergências entre avaliador e supervisor são arbitradas. A nota vencedora entra no cálculo do IMGQ.",
      n: null,
    },
    {
      num: "⑥",
      titulo: "Parecer de confiabilidade",
      desc: "Confiável · Com ressalvas · Não confiável. O selo QualiEMSERH só é oficial após a validação.",
      n: null,
    },
  ];
  return (
    <Tela title="Supervisão AUDIN" sub="3ª linha de defesa · asseguração independente" onBack={onBack}>
      <div
        className="m-card pad"
        style={{
          background: "color-mix(in srgb, var(--primary) 6%, white)",
          border: "1px solid color-mix(in srgb, var(--primary) 18%, white)",
          marginBottom: 16,
        }}
      >
        <div
          style={{ fontWeight: 800, fontSize: "var(--text-sm)", marginBottom: 6, color: "var(--primary)" }}
        >
          O que é a supervisão independente?
        </div>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", margin: 0, lineHeight: 1.5 }}>
          A AUDIN acompanha a avaliação em 6 fases. Caso haja divergências entre a nota do avaliador e a do
          supervisor (≥ 2 níveis), os itens voltarão para revisão antes de o parecer ser emitido.
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {FASES.map((f) => (
          <div
            key={f.num}
            className="m-card pad"
            style={{ display: "grid", gridTemplateColumns: "32px 1fr", gap: 12, alignItems: "start" }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "color-mix(in srgb, var(--primary) 12%, white)",
                display: "grid",
                placeItems: "center",
                fontWeight: 800,
                fontSize: 14,
                color: "var(--primary)",
                flexShrink: 0,
              }}
            >
              {f.num}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "var(--text-sm)", marginBottom: 3 }}>
                {f.titulo}
                {f.n != null && (
                  <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 6 }}>
                    · {f.n} critérios
                  </span>
                )}
              </div>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", lineHeight: 1.45 }}>
                {f.desc}
              </div>
            </div>
          </div>
        ))}
      </div>
      <p
        style={{
          fontSize: "var(--text-2xs)",
          color: "var(--text-faint)",
          textAlign: "center",
          marginTop: 20,
          lineHeight: 1.4,
        }}
      >
        Acesse como AUDIN para preencher e validar a supervisão.
      </p>
    </Tela>
  );
}

/* ---------------- Estrutura (setores × macroprocessos) ---------------- */
export function Estrutura({ onBack }: TelaProps) {
  return (
    <Tela title="Estrutura" sub={`${SETORES.length} setores · ${CATALOGO.length} itens`} onBack={onBack}>
      <div className="m-card pad" style={{ marginBottom: 12 }}>
        {Object.entries(MACROS).map(([k, nome]) => (
          <div key={k} style={{ display: "flex", gap: 8, padding: "6px 0" }}>
            <b style={{ fontFamily: "var(--font-mono)", color: "var(--primary)" }}>{k}</b>
            <span style={{ fontSize: 13 }}>{nome}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {SETORES.map((s) => (
          <div
            key={s.code}
            className="m-card pad"
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <span>
              <b>{s.nome}</b>{" "}
              <span className="spec">
                · {s.code} · macro {s.macro}
              </span>
            </span>
            <b style={{ fontFamily: "var(--font-mono)" }}>{s.itens}</b>
          </div>
        ))}
      </div>
    </Tela>
  );
}

/* ---------------- Motor de cálculo (coeficientes) ---------------- */
export function Motor({ onBack }: TelaProps) {
  const c = engine.config;
  const linhas: [string, string][] = [
    ["Ponderação do IMGQ", c.ponderacao === "ponderadaPorItens" ? "Por nº de itens" : "Média simples"],
    ["Meta por subíndice", engine.pct(c.metaSubindice)],
    ["Redução máx. do controle", engine.pct(c.residualReducao)],
    ["Piso do risco residual", engine.pct(c.residualPiso)],
    ["Teto na exclusão CORE", engine.pct(c.tetoExclusao)],
    ["Gatilho de priorização do Plano de Ação", `risco ≥ ${c.gatilhoPlano}`],
    ["Faixa QACI", `± ${engine.pct(c.qaciFaixa)}`],
    ["Faixa IMS", `± ${engine.pct(c.imsFaixa)}`],
  ];
  return (
    <Tela title="Motor de Cálculo" sub="Coeficientes vigentes (somente leitura)" onBack={onBack}>
      <div className="m-card pad">
        {linhas.map(([k, v]) => (
          <div
            key={k}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px 0",
              borderBottom: "0.5px solid var(--border)",
              fontSize: 13.5,
            }}
          >
            <span style={{ color: "var(--text-muted)" }}>{k}</span>
            <b style={{ fontFamily: "var(--font-mono)" }}>{v}</b>
          </div>
        ))}
      </div>
      <p className="spec" style={{ marginTop: 12 }}>
        A edição de coeficientes (perfil Coordenação) será habilitada com persistência no servidor.
      </p>
    </Tela>
  );
}
